import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";

export interface PermissionsMap {
  [resource_key: string]: {
    view: boolean;
    create: boolean;
    update: boolean;  // Migrado de 'edit' para 'update' após 20251127000000
    delete: boolean;
  };
}

/**
 * Hook to fetch all permissions for current user in a single RPC call
 * This replaces multiple individual permission checks for better performance
 *
 * IMPORTANTE: Após migração 20251127000000, usa get_user_all_permissions
 * que retorna features consolidadas no formato: { feature: { view, create, update, delete }, ... }
 *
 * Query key includes current_clinic_id so that when profile loads/updates after
 * first login, permissions refetch and navigation shows correct links.
 */
export function usePermissionsMap() {
  const { user } = useAuth();
  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isProfileError,
  } = useProfile();

  const isProfileOrClinicPending =
    !!user &&
    (isLoadingProfile || isProfileError || profile === undefined || !profile.current_clinic_id);

  const permissionsQuery = useQuery<PermissionsMap>({
    queryKey: ["user-permissions-map", profile?.current_clinic_id ?? null],
    queryFn: async () => {
      if (!user) return {};

      const clinicId = profile?.current_clinic_id;
      if (!clinicId) {
        return {};
      }

      const { data, error } = await supabase.rpc("get_user_all_permissions", {
        _user_id: user.id,
        _clinic_id: clinicId,
      }) as { data: PermissionsMap | null; error: unknown };

      if (error) {
        console.error("Error fetching permissions map:", error);
        return {};
      }

      const raw = data ?? {};
      const applications = raw.applications;
      const vaccines = raw.vaccines;

      // Temporary dual-read compatibility during vaccines -> applications migration.
      if (applications && !vaccines) {
        return { ...raw, vaccines: applications };
      }
      if (!applications && vaccines) {
        return { ...raw, applications: vaccines };
      }
      return raw;
    },
    enabled: !!user && profile !== undefined && !!profile?.current_clinic_id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: false,
    // Permissions may change while the app is open (for example after the
    // terms feature is seeded or a clinic role is updated). Do not keep a
    // stale `false` value when the user returns to a screen that consumes it.
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  return {
    ...permissionsQuery,
    isLoading: isProfileOrClinicPending || permissionsQuery.isLoading,
  };
}

/**
 * Hook to efficiently check multiple permissions at once
 * @param checks Array of resources and actions to check
 * @returns Object with permissions mapped by "resource_action" keys and loading state
 *
 * IMPORTANTE: Após migração 20251127000000, 'edit' é mapeado para 'update' automaticamente
 *
 * @example
 * const { permissions, isLoading } = useMultiplePermissions([
 *   { resource: 'patients', action: 'view' },
 *   { resource: 'appointments', action: 'create' },
 * ]);
 *
 * // Access: permissions.patients_view, permissions.appointments_create
 */
export function useMultiplePermissions(
  checks: Array<{ resource: string; action?: "view" | "create" | "edit" | "delete" }>
) {
  const { data: permissionsMap, isLoading } = usePermissionsMap();

  const permissions = checks.reduce(
    (acc, check) => {
      const requestedAction = check.action || "view";
      const key = `${check.resource}_${requestedAction}`;

      // Mapear 'edit' para 'update' para compatibilidade com novo sistema
      const normalizedAction = requestedAction === "edit" ? "update" : requestedAction;

      const resourcePerms = permissionsMap?.[check.resource];
      acc[key] = resourcePerms?.[normalizedAction as keyof typeof resourcePerms] ?? false;
      return acc;
    },
    {} as Record<string, boolean>
  );

  return {
    permissions,
    isLoading,
  };
}
